import React, {useState, useEffect, useRef} from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import apiClient from "../../../../services/api-client";
import StorageHelper from '../../../../utils/storage-helper';
import { Toast } from 'primereact/toast';

const ChangeStatusCooperateReceiptDialog = ({ visible, receipt, onHide, onStatusUpdate }) => {
    const [newStatus, setNewStatus] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const toast = useRef(null);

    const statusOptions = [
        { label: 'COMPLETED', value: 'COMPLETED' },
        { label: 'REVOKED', value: 'REVOKED' }
    ];

    const defaultMessages = {
        COMPLETED: 'Batch process was completed successfully',
        REVOKED: ''
    };

    // Reset form when dialog opens with new receipt
    useEffect(() => {
        if (visible && receipt) {
            setNewStatus(receipt.status);
            setNotes('');
        }
    }, [visible, receipt]);

    // Update notes when status changes with default message
    useEffect(() => {
        if (newStatus && receipt && newStatus !== receipt.status) {
            setNotes(defaultMessages[newStatus] || '');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newStatus, receipt]);

    const handleStatusChange = async () => {
        if (!receipt || !newStatus || !notes.trim()) {
            alert('Please select a status and provide notes');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                receiptNumber: receipt.receiptNumber,
                status: newStatus,
                notes: notes.trim()
            };

            const response = await apiClient.post(`cooperateReceipt/changeStatus`,
                payload,
                {
                    headers: {
                        Authorization: 'Bearer ' + StorageHelper.get('token'),
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.status === "SUCCESS") {
                onStatusUpdate(receipt.receiptNumber, newStatus);
                toast.current.show({
                        severity: 'success',
                        summary: "Status updated successfully",
                        detail: response.data.message,
                        life: 4000
                    });
                onHide();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: "Status update failed",
                    detail: response.data.message || "Something went wrong. Please try again",
                    life: 4000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: "Status update failed",
                detail: error.response?.data?.detail || error.message || "Something went wrong. Please try again",
                life: 4000
            });
        } finally {
            setSubmitting(false);
        }
    };

    const dialogFooter = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-text"
                disabled={submitting}
            />
            <Button
                label="Update Status"
                icon="pi pi-check"
                onClick={handleStatusChange}
                loading={submitting}
                disabled={!newStatus || !notes.trim() || newStatus === receipt?.status}
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                header="Change Receipt Status"
                visible={visible}
                style={{ width: '500px' }}
                onHide={onHide}
                footer={dialogFooter}
                modal
            >
                {receipt && (
                    <div className="p-fluid">
                        <div className="field" style={{ marginBottom: '1rem' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                                Receipt Number
                            </label>
                            <div style={{ padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                {receipt.receiptNumber}
                            </div>
                        </div>

                        <div className="field" style={{ marginBottom: '1rem' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                                Corporate Name
                            </label>
                            <div style={{ padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                {receipt.cooperateName}
                            </div>
                        </div>

                        <div className="field" style={{ marginBottom: '1rem' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                                Current Status
                            </label>
                            <div style={{ padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                {receipt.status}
                            </div>
                        </div>

                        <div className="field" style={{ marginBottom: '1rem' }}>
                            <label htmlFor="newStatus" style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                                New Status <span style={{ color: 'red' }}>*</span>
                            </label>
                            <Dropdown
                                id="newStatus"
                                value={newStatus}
                                options={statusOptions}
                                onChange={(e) => setNewStatus(e.value)}
                                placeholder="Select new status"
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="notes" style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                                Reason for Status Change <span style={{ color: 'red' }}>*</span>
                            </label>
                            <InputTextarea
                                id="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={4}
                                placeholder="Enter reason for changing status..."
                                style={{ resize: 'vertical' }}
                            />
                            <small style={{ color: '#6c757d', display: 'block', marginTop: '0.25rem' }}>
                                Provide a clear reason for this status change
                            </small>
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default ChangeStatusCooperateReceiptDialog;